import React, { useEffect, useState, useCallback } from 'react';
import { alpacaService, PlaybookScorecard, PlaybookScoreItem } from '../../services/AlpacaService';
import SignalLoader from '../../components/SignalLoader/SignalLoader';
import styles from './PlaybookTrackRecord.module.css';

const PlaybookTrackRecord: React.FC = () => {
  const [data, setData] = useState<PlaybookScorecard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const d = await alpacaService.getPlaybookScorecard();
      setData(d);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load playbook scorecard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) return <SignalLoader message="Loading playbook track record..." fullPage />;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!data || !data.total) {
    return (
      <div className={styles.empty}>
        No scored playbook items yet. Playbook trade ideas will be scored automatically after their holding period
        elapses.
        {data?.pending_count ? (
          <p style={{ marginTop: 8 }}>
            {data.pending_count} item{data.pending_count > 1 ? 's' : ''} pending scoring.
          </p>
        ) : null}
      </div>
    );
  }

  const pnlClass = (val: number) => (val > 0 ? styles.positive : val < 0 ? styles.negative : '');

  const renderBreakdown = (
    title: string,
    breakdown: Record<string, { total: number; correct: number; accuracy_pct: number; avg_pnl_pct: number }>,
  ) => (
    <div className={styles.breakdownCard}>
      <div className={styles.breakdownHeader}>{title}</div>
      {Object.entries(breakdown).map(([key, val]) => (
        <div key={key} className={styles.breakdownRow}>
          <span className={styles.breakdownLabel}>{key.replace('_', ' ')}</span>
          <span>
            <span className={`${styles.breakdownValue} ${pnlClass(val.avg_pnl_pct)}`}>{val.accuracy_pct}%</span>
            <span className={styles.breakdownDetail}>
              {val.correct}/{val.total} &middot; {val.avg_pnl_pct > 0 ? '+' : ''}
              {val.avg_pnl_pct}%
            </span>
          </span>
        </div>
      ))}
    </div>
  );

  const renderItem = (item: PlaybookScoreItem, i: number) => (
    <tr key={`${item.ticker}-${item.entry_date}-${i}`}>
      <td className={styles.ticker}>{item.ticker}</td>
      <td>
        <span className={`${styles.dirBadge} ${item.direction === 'long' ? styles.longBadge : styles.shortBadge}`}>
          {item.direction === 'long' ? '▲ Long' : '▼ Short'}
        </span>
      </td>
      <td>
        <span
          className={`${styles.convBadge} ${
            item.conviction === 'high'
              ? styles.convHigh
              : item.conviction === 'medium'
                ? styles.convMed
                : styles.convLow
          }`}
        >
          {item.conviction}
        </span>
      </td>
      <td className={styles.mono}>${item.entry_price?.toFixed(2)}</td>
      <td className={styles.mono}>{item.status === 'scored' ? `$${item.exit_price?.toFixed(2)}` : '—'}</td>
      <td className={`${styles.mono} ${item.status === 'scored' ? pnlClass(item.pnl_pct || 0) : ''}`}>
        {item.status === 'scored' ? `${(item.pnl_pct || 0) > 0 ? '+' : ''}${item.pnl_pct?.toFixed(2)}%` : '—'}
      </td>
      <td>
        {item.status === 'scored' ? (
          <span className={`${styles.resultBadge} ${item.direction_correct ? styles.correct : styles.incorrect}`}>
            {item.direction_correct ? 'Correct' : 'Wrong'}
          </span>
        ) : (
          <span className={`${styles.resultBadge} ${styles.pending}`}>Pending</span>
        )}
      </td>
      <td className={styles.mono}>{item.entry_date}</td>
    </tr>
  );

  return (
    <div className={styles.page}>
      {/* Stats bar */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Ideas Scored</div>
          <div className={styles.statValue}>{data.total}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Win Rate</div>
          <div className={`${styles.statValue} ${data.accuracy_pct >= 50 ? styles.positive : styles.negative}`}>
            {data.accuracy_pct}%
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Avg P&L</div>
          <div className={`${styles.statValue} ${pnlClass(data.avg_pnl_pct)}`}>
            {data.avg_pnl_pct > 0 ? '+' : ''}
            {data.avg_pnl_pct}%
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Streak</div>
          <div className={styles.statValue}>
            {data.current_streak} {data.streak_type === 'correct' ? 'W' : 'L'}
          </div>
        </div>
        {data.pending_count > 0 && (
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Pending</div>
            <div className={styles.statValue}>{data.pending_count}</div>
          </div>
        )}
      </div>

      {/* Breakdowns */}
      <h3 className={styles.sectionTitle}>Accuracy Breakdown</h3>
      <div className={styles.breakdownGrid}>
        {data.by_conviction &&
          Object.keys(data.by_conviction).length > 0 &&
          renderBreakdown('By Conviction', data.by_conviction)}
        {data.by_direction &&
          Object.keys(data.by_direction).length > 0 &&
          renderBreakdown('By Direction', data.by_direction)}
        {data.by_timeframe &&
          Object.keys(data.by_timeframe).length > 0 &&
          renderBreakdown('By Timeframe', data.by_timeframe)}
      </div>

      {/* Recent scored items */}
      {data.recent_items && data.recent_items.length > 0 && (
        <>
          <h3 className={styles.sectionTitle}>Recent Scored Ideas</h3>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th>Direction</th>
                  <th>Conviction</th>
                  <th>Entry</th>
                  <th>Exit</th>
                  <th>P&L</th>
                  <th>Result</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>{data.recent_items.map((item, i) => renderItem(item, i))}</tbody>
            </table>
          </div>
        </>
      )}

      {/* Pending items */}
      {data.pending_items && data.pending_items.length > 0 && (
        <>
          <h3 className={styles.sectionTitle}>Awaiting Scoring</h3>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th>Direction</th>
                  <th>Conviction</th>
                  <th>Entry</th>
                  <th>Exit</th>
                  <th>P&L</th>
                  <th>Result</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>{data.pending_items.map((item, i) => renderItem(item, i))}</tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default PlaybookTrackRecord;
